export interface VariantAware {
  id: string;
  name: string;
  sku: string | null;
  parentSku?: string | null;
  variantAxes?: Record<string, unknown> | null;
}

export type VariantEntry<T extends VariantAware> =
  | { kind: 'single'; product: T }
  | {
      kind: 'group';
      parentSku: string;
      baseName: string;
      axisKey: string;
      axisLabel: string;
      options: { value: string; label: string; product: T }[];
    };

const AXIS_LABELS: Record<string, string> = {
  insulation: 'Insulation',
  capacity_l: 'Capacity',
  grade: 'Grade',
  thickness_mm: 'Thickness',
};

export function humanizeAxisKey(key: string): string {
  if (AXIS_LABELS[key]) return AXIS_LABELS[key];
  const base = key.replace(/_(l|mm)$/i, '').replace(/_/g, ' ').trim();
  return base.replace(/\b\w/g, (c) => c.toUpperCase());
}

export function formatAxisValue(key: string, value: unknown): string {
  if (typeof value === 'number') {
    if (/_l$/i.test(key)) return `${value} L`;
    if (/_mm$/i.test(key)) return `${value} mm`;
  }
  return String(value);
}

function commonPrefix(names: string[]): string {
  if (names.length === 0) return '';
  let p = names[0]!;
  for (const n of names.slice(1)) {
    let i = 0;
    while (i < p.length && i < n.length && p[i] === n[i]) i++;
    p = p.slice(0, i);
  }
  return p.replace(/[\s\-–·,(]+$/, '').trim();
}

// First variantAxes key whose stringified value differs across members.
function findDifferingAxis<T extends VariantAware>(members: T[]): string | null {
  const keys = new Set<string>();
  for (const m of members) for (const k of Object.keys(m.variantAxes ?? {})) keys.add(k);
  for (const k of keys) {
    const vals = new Set(members.map((m) => String((m.variantAxes ?? {})[k])));
    if (vals.size > 1) return k;
  }
  return null;
}

export function groupProductsByVariant<T extends VariantAware>(items: T[]): VariantEntry<T>[] {
  // Preserve first-appearance order of each parentSku / singleton.
  const order: string[] = [];
  const buckets = new Map<string, T[]>();
  const singles: VariantEntry<T>[] = [];

  for (const it of items) {
    const ps = it.parentSku?.trim();
    if (!ps) {
      singles.push({ kind: 'single', product: it });
      order.push(`__single__${singles.length - 1}`);
      continue;
    }
    if (!buckets.has(ps)) {
      buckets.set(ps, []);
      order.push(ps);
    }
    buckets.get(ps)!.push(it);
  }

  const out: VariantEntry<T>[] = [];
  let singleIdx = 0;
  for (const key of order) {
    if (key.startsWith('__single__')) {
      out.push(singles[singleIdx++]!);
      continue;
    }
    const members = buckets.get(key)!;
    const axisKey = members.length >= 2 ? findDifferingAxis(members) : null;
    if (!axisKey) {
      for (const m of members) out.push({ kind: 'single', product: m });
      continue;
    }
    out.push({
      kind: 'group',
      parentSku: key,
      baseName: commonPrefix(members.map((m) => m.name)) || key,
      axisKey,
      axisLabel: humanizeAxisKey(axisKey),
      options: members.map((m) => {
        const v = (m.variantAxes ?? {})[axisKey];
        return { value: String(v), label: formatAxisValue(axisKey, v), product: m };
      }),
    });
  }
  return out;
}
